import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import * as z from 'zod';
import useLocalization from '../../../hooks/useLocalization';
import { useCrypto } from '../../../hooks/payment/useCrypto';
import { useBalances } from '../../../queries';
import { useCryptoExchangeRate } from '../../../queries/crypto';
import useMarketplaceCurrency from '../../../hooks/usePlayerCurrency';
import { createCryptoWalletAddressSchema } from '../../../schemas/helpers/cryptoValidation';
import { NeobetNumberField, NeobetTextField } from '../../skins/neobet/molecules/NeobetFormFields';
import StyledForm from '../shared/StyledForm';

const styles = {
  stepContainer: {
    gap: 2,
  },
  amountGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  estimateRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    backgroundColor: 'neutral.500',
    borderRadius: '0 0 8px 8px',
    px: 2,
    py: 1,
  },
  estimateValue: {
    typography: 'body3',
    fontWeight: 700,
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  estimateRate: {
    typography: 'caption',
    color: 'text.secondary',
  },
  estimateUnavailable: {
    typography: 'caption',
    color: 'text.disabled',
  },
  amountFieldWithEstimate: {
    '& .MuiOutlinedInput-root': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  formContainer: {
    justifyContent: 'space-between',
    flex: 1,
  },
} as const;

const withdrawSchema = (t: TFunction, walletBalance: number) =>
  z.object({
    amount: z.coerce
      .number({ error: t('invalidNumber') })
      .positive(t('amountMustBeGreaterThanZero'))
      .max(walletBalance, t('insufficientFunds')),
    address: createCryptoWalletAddressSchema(t),
  });

type WithdrawFormInput = z.input<ReturnType<typeof withdrawSchema>>;
type WithdrawFormData = z.output<ReturnType<typeof withdrawSchema>>;

type CryptoWithdrawFormProps = {
  paymentProvider: string | null;
};

const CryptoWithdrawForm = ({ paymentProvider }: CryptoWithdrawFormProps) => {
  const { t } = useLocalization();
  const { mainBalance: { balance: walletBalance } = { balance: 0 } } = useBalances();
  const {
    withdrawMutation: { mutate: withdraw, isPending: isWithdrawPending },
  } = useCrypto();

  const currency = useMarketplaceCurrency();
  const { data: exchangeRateData, isLoading: isRateLoading, isError: isRateError } = useCryptoExchangeRate(currency);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<WithdrawFormInput, unknown, WithdrawFormData>({
    resolver: zodResolver(withdrawSchema(t, walletBalance)),
    defaultValues: {
      amount: undefined,
      address: '',
    },
  });

  const watchedAmount = useWatch({ control, name: 'amount' });
  const numericAmount = Number(watchedAmount) || 0;
  const rate = exchangeRateData?.rate;
  const estimatedUsdt = rate && numericAmount > 0 ? numericAmount * rate : null;

  const showEstimate = currency !== 'USDT' && numericAmount > 0 && !errors.amount;
  const showEstimateUnavailable = showEstimate && !isRateLoading && isRateError;
  const showEstimateRate = showEstimate && !isRateLoading && !isRateError && estimatedUsdt !== null;
  const showEstimateRow = showEstimateUnavailable || showEstimateRate;

  const handleWithdrawSubmit: SubmitHandler<WithdrawFormData> = (data) => {
    withdraw({
      amount: String(data.amount),
      address: data.address.trim(),
    });
  };

  return (
    <StyledForm
      submitTitle={t('continue')}
      onSubmit={handleSubmit(handleWithdrawSubmit)}
      isSubmitting={isWithdrawPending}
      disablePadding
      actionsSx={{ pb: 0 }}
      sx={styles.formContainer}
    >
      <Stack sx={styles.stepContainer}>
        <Box sx={styles.amountGroup}>
          <NeobetNumberField
            control={control}
            name='amount'
            label={t('amount')}
            fullWidth
            disabled={isWithdrawPending}
            sx={showEstimateRow ? styles.amountFieldWithEstimate : undefined}
          />
          {showEstimateRow && (
            <Box sx={styles.estimateRow}>
              {showEstimateUnavailable && (
                <Typography sx={styles.estimateUnavailable}>{t('rateUnavailable')}</Typography>
              )}
              {showEstimateRate && (
                <>
                  <Typography sx={styles.estimateValue}>≈ {estimatedUsdt.toFixed(2)} USDT</Typography>
                  <Typography sx={styles.estimateRate}>
                    {t('exchangeRate')}: 1 {currency} = {rate?.toFixed(4)} USDT
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
        <NeobetTextField
          name='address'
          control={control}
          label={t('walletAddress')}
          fullWidth
          error={!!errors.address}
          helperText={errors.address?.message}
          disabled={isWithdrawPending}
        />
      </Stack>
    </StyledForm>
  );
};

export default CryptoWithdrawForm;
